import React from 'react'
import i18n from "i18n";

import ROUTES from '@shared/lib/constants/routes';
import LoginForm from '@shared/components/Login/LoginForm';
import RegistrationForm from '@shared/components/Login/RegistrationForm';
import { AUTH_EVENTS } from './auth-events'
import { useAuth } from './AuthProvider'

import 'resources/css/auth-overrides.css';
import './AuthModals.scss'

function Modal({ onClose, children }) {
  React.useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return (
    <div className="c7-modal-backdrop" role="dialog" aria-modal="true" onMouseDown={onClose}>
      <div className="c7-modal c7-modal-auth" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
        <button className="c7-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <div className="c7-modal-body">{children}</div>
      </div>
    </div>
  )
}

function LoginCard({ onSwitchToSignup, onSuccess }) {
  return (
    <section className="section auth-page">
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center justify-center py-8 md:py-12">
          <div className="w-full md:w-5/6 lg:w-2/3 xl:w-1/2">
            <div className="c7-auth-card rounded-xl shadow-lg border p-6 max-w-md mx-auto" id="card-l-r">
              <div className="flex justify-between items-start pb-4 mb-4 border-b" style={{ borderColor: 'var(--c7-modal-border)' }}>
                <div>
                  <h5 className="text-xl font-semibold mb-1">{i18n.t('login')}</h5>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className="text-sm c7-auth-card-muted">{i18n.t('sign_in')}</span>
                  <a
                    className="text-[#0b8e36] font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-[#0b8e36] rounded"
                    href={ROUTES.REGISTER}
                    onClick={(e) => {
                      e.preventDefault();
                      onSwitchToSignup?.();
                    }}
                  >
                    {i18n.t('auth.signupPrompt')}
                  </a>
                </div>
              </div>
              <LoginForm onSuccess={onSuccess} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function RegistrationCard({ onSwitchToLogin, onSuccess }) {
  return (
    <section className="section auth-page">
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center justify-center py-8 md:py-12">
          <div className="w-full md:w-5/6 lg:w-2/3 xl:w-1/2">
            <div className="c7-auth-card rounded-xl shadow-lg border p-6 max-w-md mx-auto" id="card-l-r">
              <div className="flex justify-between items-start pb-4 mb-4 border-b" style={{ borderColor: 'var(--c7-modal-border)' }}>
                <div>
                  <h5 className="text-xl font-semibold mb-1">{i18n.t('register')}</h5>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className="text-sm c7-auth-card-muted">{i18n.t('register_now')}</span>
                  <a
                    className="text-[#0b8e36] font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-[#0b8e36] rounded"
                    href={ROUTES.LOGIN}
                    onClick={(e) => {
                      e.preventDefault();
                      onSwitchToLogin?.();
                    }}
                  >
                    {i18n.t('auth.loginPrompt')}
                  </a>
                </div>
              </div>
              <RegistrationForm onSuccess={onSuccess} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AuthModalHost() {
  const [open, setOpen] = React.useState(false)
  const [mode, setMode] = React.useState('login') // 'login' | 'signup'
  const { applyAuthPayload, refreshAuth } = useAuth()

  React.useEffect(() => {
    const handler = (event) => {
      const nextMode = event?.detail?.mode === 'signup' ? 'signup' : 'login'
      setMode(nextMode)
      setOpen(true)
    }
    window.addEventListener(AUTH_EVENTS.OPEN, handler)
    return () => window.removeEventListener(AUTH_EVENTS.OPEN, handler)
  }, [])

  const handleClose = React.useCallback(() => {
    setOpen(false)
  }, [])

  const handleSuccess = React.useCallback((payload) => {
    applyAuthPayload(payload)
    setOpen(false)
    refreshAuth()
  }, [applyAuthPayload, refreshAuth])

  if (!open) return null

  return mode === 'signup' ? (
    <Modal onClose={handleClose}>
      <RegistrationCard onSuccess={handleSuccess} onSwitchToLogin={() => setMode('login')} />
    </Modal>
  ) : (
    <Modal onClose={handleClose}>
      <LoginCard onSuccess={handleSuccess} onSwitchToSignup={() => setMode('signup')} />
    </Modal>
  )
}

